import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  LinearProgress,
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import {
  ScrapEyeSensorConnectionRead,
  useConnectSensorMutation,
  useCorrectSensorConnectionMutation,
  useListScrapEyeDevicesQuery,
  useListSensorConnectionsQuery,
  useListSensorCameraRecordingsQuery,
  useListSensorsQuery,
} from "src/store/api/generatedApi";
import { InstallationViewToggle } from "./InstallationViewToggle";
import { formatInstallationTimestamp } from "./installationTimestamp";
import { SensorEditorDialog } from "./SensorEditorDialog";
import { SensorInterval, SensorIntervalFields } from "./SensorIntervalFields";

interface ConnectionEditorProps {
  connection: ScrapEyeSensorConnectionRead | null;
  onClose: () => void;
}

export const ConnectionEditor = ({
  connection,
  onClose,
}: ConnectionEditorProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const { data: sensors, error: sensorError } = useListSensorsQuery({
    tenantName,
  });
  const { data: devices } = useListScrapEyeDevicesQuery({
    tenantName,
    includeAdditionalInfo: false,
  });
  const [interval, setInterval] = useState<SensorInterval>(() => ({
    sensorId: connection?.sensor_id ?? null,
    startedAt: connection?.connected_at ?? dayjs().toISOString(),
    endedAt: connection?.disconnected_at ?? null,
  }));
  const [deviceId, setDeviceId] = useState<number | null>(
    connection?.device_id ?? null
  );
  const [deviceSensorId, setDeviceSensorId] = useState(
    connection?.device_sensor_id ?? ""
  );
  const [cameraId, setCameraId] = useState<number | null>(
    connection?.camera_id ?? null
  );
  const { data: cameraRecordings } = useListSensorCameraRecordingsQuery(
    { tenantName, deviceId: deviceId ?? 0 },
    { skip: deviceId == null }
  );
  const selectedSensor = sensors?.items.find(
    (sensor) => sensor.id === interval.sensorId
  );

  const [validInterval, setValidInterval] = useState(false);
  const [connect, { isLoading: isCreating }] = useConnectSensorMutation();
  const [correct, { isLoading: isCorrecting }] =
    useCorrectSensorConnectionMutation();
  const busy = isCreating || isCorrecting;

  const save = async () => {
    if (
      interval.sensorId == null ||
      selectedSensor == null ||
      deviceId == null ||
      !validInterval
    )
      return;
    const connectionRecord = {
      sensor_id: interval.sensorId,
      device_id: deviceId,
      device_sensor_id: deviceSensorId.trim() || null,
      camera_id: selectedSensor.sensor_type === "camera" ? cameraId : null,
      connected_at: interval.startedAt,
      disconnected_at: interval.endedAt,
    };
    if (connection == null) {
      await connect({
        tenantName,
        scrapEyeSensorConnectionCreate: connectionRecord,
      }).unwrap();
    } else {
      await correct({
        tenantName,
        versionId: connection.id,
        scrapEyeSensorConnectionCorrection: connectionRecord,
      }).unwrap();
    }
  };

  return (
    <SensorEditorDialog
      title={t(connection == null ? "sensorConnectButton" : "edit")}
      busy={busy}
      valid={validInterval && deviceId != null && selectedSensor != null}
      onClose={onClose}
      onSave={save}
      errorMessage={t("sensorConnectError")}
    >
      {sensorError != null && (
        <Alert severity="error">{t("sensorHistoryLoadError")}</Alert>
      )}
      <SensorIntervalFields
        sensors={sensors?.items ?? []}
        interval={interval}
        onChange={(nextInterval) => {
          if (nextInterval.sensorId !== interval.sensorId) {
            setCameraId(null);
            setDeviceSensorId("");
          }
          setInterval(nextInterval);
        }}
        onValidityChange={setValidInterval}
        startLabel={t("sensorConnectedAtLabel")}
        endLabel={t("sensorDisconnectedAtLabel")}
        disabled={busy}
      />
      <TextField
        select
        label={t("installationDeviceLabel")}
        value={deviceId ?? ""}
        disabled={busy}
        onChange={(event) => {
          setDeviceId(Number(event.target.value));
          setCameraId(null);
          setDeviceSensorId("");
        }}
      >
        {devices?.items.map((device) => (
          <MenuItem key={device.id} value={device.id}>
            {device.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t("sensorConnectionPortLabel")}
        value={deviceSensorId}
        disabled={busy || cameraId != null}
        onChange={(event) => {
          setDeviceSensorId(event.target.value);
        }}
      />
      {selectedSensor?.sensor_type === "camera" && (
        <TextField
          select
          label={t("sensorCameraFootageLabel")}
          value={cameraId ?? ""}
          disabled={busy || deviceId == null}
          onChange={(event) => {
            const selectedId =
              event.target.value === "" ? null : Number(event.target.value);
            setCameraId(selectedId);
            const selected = cameraRecordings?.items.find(
              (candidate) => candidate.id === selectedId
            );
            if (selected != null) setDeviceSensorId(selected.physical_id);
          }}
        >
          <MenuItem value="">{t("sensorNoCameraFootage")}</MenuItem>
          {cameraRecordings?.items.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.physical_id} ·{" "}
              {formatInstallationTimestamp(candidate.created_at)}
            </MenuItem>
          ))}
        </TextField>
      )}
    </SensorEditorDialog>
  );
};

export const SensorConnectionsSection = () => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [view, setView] = useNavigationQuery(
    "connectionRecords",
    "current",
    choiceQuery(["current", "all"] as const),
    "replace"
  );
  const [editing, setEditing] = useState<{
    connection: ScrapEyeSensorConnectionRead | null;
  } | null>(null);
  const {
    data: connections,
    isFetching,
    error,
  } = useListSensorConnectionsQuery({ tenantName, kind: view });
  return (
    <Card variant="outlined">
      <CardHeader title={t("sensorConnectionsTitle")} />
      <CardContent>
        <Stack spacing={2}>
          <InstallationViewToggle value={view} onChange={setView} />
          {isFetching && <LinearProgress />}
          {error != null && (
            <Alert severity="error">
              {fetchErrorDetail(error) ?? t("sensorHistoryLoadError")}
            </Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("sensorNameLabel")}</TableCell>
                <TableCell>{t("installationDeviceLabel")}</TableCell>
                <TableCell>{t("sensorConnectionPortLabel")}</TableCell>
                <TableCell>{t("sensorConnectedAtLabel")}</TableCell>
                <TableCell>{t("sensorDisconnectedAtLabel")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {connections?.items.map((connection) => (
                <TableRow key={connection.id}>
                  <TableCell>{connection.sensor_name}</TableCell>
                  <TableCell>{connection.device_name}</TableCell>
                  <TableCell>{connection.device_sensor_id}</TableCell>
                  <TableCell>
                    {formatInstallationTimestamp(connection.connected_at)}
                  </TableCell>
                  <TableCell>
                    {connection.disconnected_at == null
                      ? null
                      : formatInstallationTimestamp(connection.disconnected_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        setEditing({ connection });
                      }}
                    >
                      {t("edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            onClick={() => {
              setEditing({ connection: null });
            }}
          >
            {t("sensorConnectButton")}
          </Button>
        </Stack>
      </CardContent>
      {editing != null && (
        <ConnectionEditor
          connection={editing.connection}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
};
